import { unwrap } from "@sagot/api-client";
import { CONSOLE_PATHS, PRODUCT_NAME, RESEND_COOLDOWN_SECONDS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Icon, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useSearchParams } from "react-router";
import { useErrorMessage } from "../lib/api-errors";
import { useCountdown } from "../lib/use-countdown";
import { readPendingAuth } from "../session/pending-auth";
import { useApi } from "../session/session-provider";
import { AuthLayout } from "./components/auth-layout";
import { FormAlert } from "./components/form-alert";
import { IconBadge } from "./components/icon-badge";
import { OtpInput, isCompleteOtp } from "./components/otp-input";
import { useCompleteSignIn } from "./use-sign-in";

function ResendRow({ email }: { email: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const { remaining, restart } = useCountdown(RESEND_COOLDOWN_SECONDS);
  const [sending, setSending] = useState(false);
  const resend = async () => {
    setSending(true);
    try {
      await unwrap(api.POST("/v1/auth/verify-email/resend", { body: { email } }));
      toast.success(t("auth.verify.resent", { email }));
      restart();
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-fg-muted">{t("auth.verify.didntGetIt")}</span>
      {remaining > 0 ? (
        <span className="font-semibold text-fg-subtle" aria-live="polite">
          {t("auth.verify.resendIn", { seconds: remaining })}
        </span>
      ) : (
        <button type="button" disabled={sending} className="cursor-pointer font-semibold text-primary-ink hover:underline" onClick={() => void resend()}>
          {t("auth.verify.resend")}
        </button>
      )}
    </div>
  );
}

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const api = useApi();
  const [params] = useSearchParams();
  const pending = readPendingAuth();
  const email = params.get("email") ?? (pending?.kind === "verify-email" ? pending.email : "");
  const completeSignIn = useCompleteSignIn();
  const message = useErrorMessage();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!isCompleteOtp(code) || !email) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = await unwrap(api.POST("/v1/auth/verify-email", { body: { email, code } }));
      await completeSignIn(body, { email, next: CONSOLE_PATHS.onboarding });
    } catch (caught) {
      setError(message(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title={t("auth.verify.title", { product: PRODUCT_NAME })} back={{ label: t("auth.common.back"), href: CONSOLE_PATHS.register }}>
      <IconBadge icon="mail" />
      <h1 className="mt-5 text-2xl font-extrabold tracking-tight">{t("auth.verify.heading")}</h1>
      <p className="mt-1 text-fg-muted">
        {t("auth.verify.sentLead")} <strong className="break-words text-fg">{email || t("auth.verify.yourEmail")}</strong>. {t("auth.verify.sentTail")}
      </p>
      {!email ? <FormAlert tone="info" title={t("auth.verify.missingEmail")} className="mt-5" /> : null}
      {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} className="mt-5" /> : null}
      <form className="mt-7" noValidate onSubmit={(event) => void onSubmit(event)}>
        <label className="label" htmlFor="otp-1">
          {t("auth.verify.codeLabel")}
        </label>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- the code is the page's only task (mockup autofocus) */}
        <OtpInput id="otp-1" value={code} onChange={setCode} autoFocus invalid={Boolean(error)} describedBy="otp-hint" />
        <p className="hint" id="otp-hint">
          {t("auth.verify.tip")}
        </p>
        <Button type="submit" size="lg" className="mt-5 w-full" loading={submitting} disabled={submitting || !isCompleteOtp(code) || !email}>
          {t("auth.verify.submit")}
        </Button>
      </form>
      <div className="mt-6 rounded-lg border border-line bg-surface px-4 py-3 text-sm">
        {email ? <ResendRow email={email} /> : null}
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-fg-muted">{t("auth.verify.wrongAddress")}</span>
          <AppLink href={CONSOLE_PATHS.register} className="font-semibold text-primary-ink hover:underline">
            {t("auth.verify.changeEmail")}
          </AppLink>
        </div>
      </div>
      <p className="mt-4 flex items-start gap-2 text-xs text-fg-subtle">
        <Icon name="info" className="mt-0.5 size-3.5" />
        {t("auth.verify.spamNote")}
      </p>
    </AuthLayout>
  );
}
